import { IonNav } from '@ionic/react';
import { ReactElement, createContext, createElement, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Tabs } from '../exports/Enums';
import MainLayout from '../layouts/MainLayout';
import collect from 'collect.js';
import { BrowserRouter } from 'react-router-dom';
import Cart from '../pages/cart';
import ModalRouter from './ModalRouter';
import ModalRoute from '../components/modals/ModalRoute';
import OrderDetails from '../pages/order-details';
import OrderConfirmationPage from '../pages/checkout/OrderConfirmationPage';
import OrderPaymentPage from '../pages/checkout/OrderPaymentPage';
import Rewards from '../pages/rewards';
import UploadGoogleReview from '../pages/upload-google-review';
import GlobalModalsStack from '../../../modals/GlobalModalsStack';
import useUrlParams from '../../../hooks/utility/useUrlParams';
import GiftCardsCheckoutPage from '../pages/gift-cards/GiftCardsCheckoutPage';
import GiftCardsOrderDetailsPage from '../pages/gift-cards/GiftCardsOrderDetailsPage';
import { AdyenRedirectResultHandler } from '../../../hooks/payment/useHandleAdyenRedirectResult';
import { SpreedlyRedirectResultHandler } from '../../../hooks/payment/useHandleSpreedlyRedirectResult';

interface IStack {
  path: Routes;
  component: React.ComponentType<any>;
}

export interface IRouterContext {
  navigation: HTMLIonNavElement;
  tab: Tabs;
  routerStack: Routes[];
  navigate: (path: Routes, payload?: any) => void;
  reset: () => void;
  goBack: () => void;
  setTab: (tab: Tabs) => void;
  getActivePage: () => Routes;
  isPage: (page: Routes) => boolean;
}

export const RouterContext = createContext<IRouterContext | null>(null);

const Router = (): ReactElement => {
  const navRef = useRef<HTMLIonNavElement>(null);
  const pushedPagesRef = useRef(0);
  const [tab, setTab] = useState<Tabs>(Tabs.Home);
  const [routerStack, setRouterStack] = useState<Routes[]>([Routes.Main]);

  const params = useUrlParams();
  const stack: IStack[] = [
    {
      path: Routes.Main,
      component: MainLayout,
    },
    {
      path: Routes.Cart,
      component: Cart,
    },
    {
      path: Routes.OrderDetails,
      component: OrderDetails,
    },
    {
      path: Routes.Checkout,
      component: OrderConfirmationPage,
    },
    {
      path: Routes.Payment,
      component: OrderPaymentPage,
    },
    {
      path: Routes.Rewards,
      component: Rewards,
    },
    {
      path: Routes.UploadGoogleReview,
      component: UploadGoogleReview,
    },
    {
      path: Routes.GiftCardCheckout,
      component: GiftCardsCheckoutPage,
    },
    {
      path: Routes.GiftCardOrderDetails,
      component: GiftCardsOrderDetailsPage,
    },
  ];

  const findStack = (path: Routes): IStack => {
    return collect(stack).firstWhere('path', path);
  };

  const navigate = (path: Routes, payload?: any): void => {
    if (!navRef.current) return;

    const { component } = findStack(path);

    navRef.current.push(() => createElement(component, payload ?? {}));
    pushedPagesRef.current += 1;
    setRouterStack((prev) => [...prev, path]);
  };

  // A double tap on Back reaches here while the first pop is still animating and ion-nav still
  // counts both views, so canGoBack() would allow a second pop that empties the stack (SHOP-WEB-6T).
  // The ref is updated synchronously, so the second tap sees the pop already spent.
  const goBack = (): void => {
    if (!navRef.current || pushedPagesRef.current === 0) return;

    pushedPagesRef.current -= 1;
    navRef.current.pop().catch(() => undefined);
    setRouterStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
  };

  const reset = (): void => {
    if (!navRef.current) return;

    pushedPagesRef.current = 0;
    navRef.current.popToRoot();
    setRouterStack([Routes.Main]);
  };

  const getActivePage = (): Routes => collect(routerStack).last() ?? Routes.Main;

  const isPage = (page: Routes): boolean => getActivePage() === page;

  const context = useMemo(
    () => ({
      navigation: navRef.current,
      tab,
      routerStack,
      navigate,
      reset,
      goBack,
      setTab,
      getActivePage,
      isPage,
    }),
    [navRef.current, tab, routerStack],
  );

  useEffect(() => {
    navRef.current.setRoot(() => <MainLayout />);

    if (!params.navigate) {
      return;
    }

    const { navigate: path, ...payload } = params;

    Promise.try(() => navigate(path, payload)).then(() =>
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000),
    );
  }, []);

  return (
    <BrowserRouter>
      <RouterContext.Provider value={context}>
        <ModalRouter>
          <IonNav ref={navRef} />
          <ModalRoute />
          <AdyenRedirectResultHandler />
          <SpreedlyRedirectResultHandler />
        </ModalRouter>
        <GlobalModalsStack />
      </RouterContext.Provider>
    </BrowserRouter>
  );
};

export default Router;
